import React from 'react';

import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';

import { TEXT_TRANSFORM_NONE } from '../Commission/AgentHubCommissions/CommissionDialog.types';

export type RewardPreviewDetails = {
  name: string;
  description: string;
  rewardType: string;
  targetAudience: string;
  operator: string;
  startDate: string;
  endDate: string;
  expiryDate: string;
  rewardBreakdown: string;
  commissionPercentage: string;
  commissionType: string;
  commissionDescription: string;
  regions: string;
  commissionStartDate: string;
  commissionEndDate: string;
  limit: string;
  valueLimits: string;
  includesTaxesAndFees: string;
  includesMargin: string;
  commissionBreakdown: string;
};

interface Props {
  open: boolean;
  isCreate: boolean;
  isSaving: boolean;
  details: RewardPreviewDetails | null;
  onBack: () => void;
  onConfirm: () => void;
}

function renderPreviewRow(label: string, value: React.ReactNode) {
  return (
    <Typography variant="body2">
      <Box component="span" sx={{ fontWeight: 'fontWeightBold' }}>
        {label}:
      </Box>{' '}
      {value}
    </Typography>
  );
}

export default function RewardPreviewDialog({ open, isCreate, isSaving, details, onBack, onConfirm }: Props) {
  return (
    <Dialog open={open} onClose={onBack} fullWidth maxWidth="md">
      <DialogTitle>Preview campaign</DialogTitle>
      <DialogContent>
        {details && (
          <Stack spacing={1} sx={{ pt: 1 }}>
            {!isCreate && (
              <Alert severity="warning">
                Saving will check all users for eligibility again. This can affect current campaigns.
              </Alert>
            )}
            <Typography variant="subtitle1" sx={{ fontWeight: 'fontWeightBold' }}>
              Campaign
            </Typography>
            {renderPreviewRow('Name', details.name)}
            {renderPreviewRow('Description', details.description)}
            {renderPreviewRow('Reward type', details.rewardType)}
            {renderPreviewRow('Target audience', details.targetAudience)}
            {renderPreviewRow('Condition operator', details.operator)}
            {renderPreviewRow('Start date', details.startDate)}
            {renderPreviewRow('End date', details.endDate)}
            {renderPreviewRow('Expiry date', details.expiryDate)}
            {renderPreviewRow('Reward breakdown', details.rewardBreakdown || '-')}
            <Typography variant="subtitle1" sx={{ pt: 1, fontWeight: 'fontWeightBold' }}>
              Commission
            </Typography>
            {renderPreviewRow('Percentage', details.commissionPercentage || '-')}
            {renderPreviewRow('Type', details.commissionType)}
            {renderPreviewRow('Description', details.commissionDescription || '-')}
            {renderPreviewRow('Regions', details.regions)}
            {renderPreviewRow('Start date', details.commissionStartDate)}
            {renderPreviewRow('End date', details.commissionEndDate)}
            {renderPreviewRow('Limit', details.limit)}
            {renderPreviewRow('Value limits', details.valueLimits || '-')}
            {renderPreviewRow('Includes taxes and fees', details.includesTaxesAndFees)}
            {renderPreviewRow('Includes margin', details.includesMargin)}
            {renderPreviewRow('Commission breakdown', details.commissionBreakdown || '-')}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button sx={TEXT_TRANSFORM_NONE} variant="text" onClick={onBack} disabled={isSaving}>
          Back
        </Button>
        <Button sx={TEXT_TRANSFORM_NONE} variant="contained" color="primary" onClick={onConfirm} disabled={isSaving}>
          {isSaving ? 'Saving...' : isCreate ? 'Create campaign' : 'Save changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
